/**
 * Module Registry
 *
 * Central registry for all CRM module manifests.
 * Provides methods to access and query module configurations.
 */

import type { ModuleManifest, ModuleRegistry } from "./types";

// Import all module manifests
import { accountsManifest } from "./accounts.manifest";
import { contactsManifest } from "./contacts.manifest";
import { opportunitiesManifest } from "./opportunities.manifest";
import { eventsManifest } from "./events.manifest";
import { documentsManifest } from "./documents.manifest";
import { productsManifest } from "./products.manifest";
import { salesOrdersManifest } from "./sales-orders.manifest";
import { salesOrderDetailsManifest } from "./sales-order-details.manifest";
import { offersManifest } from "./offers.manifest";
import { offerDetailsManifest } from "./offer-details.manifest";
import { invoicesManifest } from "./invoices.manifest";
import { invoiceDetailsManifest } from "./invoice-details.manifest";
import { projectsManifest } from "./projects.manifest";
import { projectTasksManifest } from "./project-tasks.manifest";

// All registered manifests
const manifests: ModuleManifest[] = [
  accountsManifest,
  contactsManifest,
  opportunitiesManifest,
  eventsManifest,
  documentsManifest,
  productsManifest,
  salesOrdersManifest,
  salesOrderDetailsManifest,
  offersManifest,
  offerDetailsManifest,
  invoicesManifest,
  invoiceDetailsManifest,
  projectsManifest,
  projectTasksManifest,
];

// Build module map for fast lookups
const modulesByName = new Map<string, ModuleManifest>();
const modulesByCoreBOS = new Map<string, ModuleManifest>();

manifests.forEach((manifest) => {
  modulesByName.set(manifest.name, manifest);
  modulesByCoreBOS.set(manifest.coreBOSModule, manifest);
});

/**
 * Get a module manifest by its internal name
 */
export function getModuleByName(name: string): ModuleManifest | undefined {
  return modulesByName.get(name);
}

/**
 * Get a module manifest by its coreBOS module name
 */
export function getModuleByCoreBOSName(
  coreBOSModule: string
): ModuleManifest | undefined {
  return modulesByCoreBOS.get(coreBOSModule);
}

/**
 * Get all registered module manifests
 */
export function getAllModules(): ModuleManifest[] {
  return manifests;
}

/**
 * Get modules that should appear in navigation
 */
export function getNavigationModules(): ModuleManifest[] {
  return manifests
    .filter((m) => !m.navigation.hidden)
    .sort((a, b) => a.navigation.order - b.navigation.order);
}

/**
 * Get modules that support context
 */
export function getContextModules(): ModuleManifest[] {
  return manifests.filter((m) => m.context.supported);
}

/**
 * Get modules by navigation group
 */
export function getModulesByGroup(group: string): ModuleManifest[] {
  return manifests
    .filter((m) => m.navigation.group === group)
    .sort((a, b) => a.navigation.order - b.navigation.order);
}

/**
 * Find module by routing keyword
 */
export function findModuleByKeyword(
  keyword: string
): ModuleManifest | undefined {
  const lowerKeyword = keyword.toLowerCase();
  return manifests.find((m) =>
    m.routing.keywords.some((k) => k.toLowerCase() === lowerKeyword)
  );
}

/**
 * Get all unique navigation groups
 */
export function getNavigationGroups(): string[] {
  const groups = new Set<string>();
  manifests.forEach((m) => {
    if (m.navigation.group) {
      groups.add(m.navigation.group);
    }
  });
  return Array.from(groups);
}

/**
 * Get WSID prefix for a module
 */
export function getWSIDPrefix(moduleName: string): string | undefined {
  return modulesByName.get(moduleName)?.wsidPrefix;
}

/**
 * Parse a WSID and return module info
 */
export function parseWSID(
  wsid: string
): { module: ModuleManifest; numericId: string } | undefined {
  const match = wsid.match(/^(\d+)x(\d+)$/);
  if (!match) return undefined;

  const [, prefix, numericId] = match;
  const module = manifests.find((m) => m.wsidPrefix === `${prefix}x`);

  if (!module) return undefined;
  return { module, numericId };
}

/**
 * Full registry object (for backwards compatibility)
 */
export const moduleRegistry: ModuleRegistry = {
  modules: modulesByName,
  getByName: getModuleByName,
  getByCoreBOSModule: getModuleByCoreBOSName,
  getAll: getAllModules,
  getNavigationItems: getNavigationModules,
};

// Re-export types
export type { ModuleManifest, ModuleRegistry } from "./types";

// Re-export individual manifests for direct access
export {
  accountsManifest,
  contactsManifest,
  opportunitiesManifest,
  eventsManifest,
  documentsManifest,
  productsManifest,
  salesOrdersManifest,
  salesOrderDetailsManifest,
  offersManifest,
  offerDetailsManifest,
  invoicesManifest,
  invoiceDetailsManifest,
  projectsManifest,
  projectTasksManifest,
};
